/**
 * Sidebar - Navigation sidebar component
 */

import React, { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';
import { ForgeDrawer, ForgeList, ForgeListItem, ForgeIcon } from '@tylertech/forge-react';
import '@tylertech/forge/drawer';
import '@tylertech/forge/list';
import '@tylertech/forge/icon';
import { Link, useLocation } from '@tanstack/react-router';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  selectSidebarCollapsed,
  selectSidebarWidth,
  setSidebarWidth,
} from '../../store/features/ui/uiSlice';
import { SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH } from '../../lib/sidebarBootstrap';
import { usePermission } from '../../hooks/usePermission';
import type { Permission } from '../../lib/permissions';
import './Sidebar.css';

/** Inline style carrying the `--forge-drawer-width` custom property `Sidebar.css` reads. */
interface SidebarDrawerStyle extends CSSProperties {
  '--forge-drawer-width'?: string;
}

/** Keyboard resize step (px) for the drag handle, per arrow-key press (#1604 a11y). */
const KEYBOARD_RESIZE_STEP = 16;

export interface SidebarProps {
  slot?: string;
}

/**
 * A navigation node. Recursive so a node can nest arbitrarily many levels of
 * children (issue #1538 needed a 2nd nesting level to express e.g.
 * Technologies > Capabilities > Domains; nothing caps it at a fixed depth).
 *
 * `id` is a stable, globally-unique key for expand/collapse state and React
 * list keys. It's distinct from `path` because a group's own route can equal
 * one of its children's route by design (e.g. the "Technologies" top group
 * and its "Products" child both point at `/technologies`) — keying expand
 * state by `path` in that case would make toggling one control both.
 *
 * `ownRoute` marks a node whose own `path` is a real, navigable page even
 * though it also has children — its header renders as a `Link` rather than
 * a plain label. (When a node's own path already matches one of its direct
 * children's paths, e.g. the Applications/Technologies top groups above,
 * that's detected automatically and `ownRoute` doesn't need to be set.)
 */
interface NavNode {
  id: string;
  path: string;
  label: string;
  icon: string;
  permission?: Permission;
  ownRoute?: boolean;
  children?: NavNode[];
}

const navItems: NavNode[] = [
  { id: 'dashboard', path: '/', label: 'Dashboard', icon: 'dashboard' },
  {
    id: 'applications',
    path: '/services',
    label: 'Applications',
    icon: 'settings',
    children: [
      {
        id: 'applications-services',
        path: '/services',
        label: 'Service Catalog',
        icon: 'view_list',
      },
      { id: 'applications-projects', path: '/projects', label: 'Projects', icon: 'folder' },
    ],
  },
  {
    id: 'business',
    path: '/business',
    label: 'Business',
    icon: 'domain',
    children: [
      {
        id: 'business-capabilities',
        path: '/capabilities',
        label: 'Capabilities',
        icon: 'architecture',
        ownRoute: true,
        children: [
          {
            id: 'business-capabilities-objectives',
            path: '/business-objectives',
            label: 'Objectives',
            icon: 'flag',
          },
        ],
      },
      { id: 'business-units', path: '/business-units', label: 'Units', icon: 'corporate_fare' },
      {
        id: 'business-stakeholders',
        path: '/stakeholders',
        label: 'Stakeholders',
        icon: 'group_work',
      },
      {
        id: 'business-processes',
        path: '/business-processes',
        label: 'Processes',
        icon: 'account_tree',
        ownRoute: true,
        children: [
          {
            id: 'business-processes-physical',
            path: '/physical-processes',
            label: 'Physical Processes',
            icon: 'factory',
          },
        ],
      },
      {
        id: 'business-environment-factors',
        path: '/business-environment-factors',
        label: 'Environment Factors',
        icon: 'public',
      },
      {
        id: 'business-maturity',
        path: '/maturity/frameworks',
        label: 'Maturity',
        icon: 'show_chart',
      },
    ],
  },
  {
    id: 'technologies',
    path: '/technologies',
    label: 'Technologies',
    icon: 'memory',
    children: [
      {
        id: 'technologies-products',
        path: '/technologies',
        label: 'Products',
        icon: 'memory',
        ownRoute: true,
        children: [
          {
            id: 'technologies-products-families',
            path: '/technology-families',
            label: 'Product Families',
            icon: 'category',
          },
          {
            id: 'technologies-products-suppliers',
            path: '/suppliers',
            label: 'Suppliers',
            icon: 'business',
          },
        ],
      },
      {
        id: 'technologies-radar',
        path: '/technologies/radar',
        label: 'Tech Radar',
        icon: 'chart_arc',
      },
      {
        id: 'technologies-capabilities',
        path: '/technology-capabilities',
        label: 'Tech Capabilities',
        icon: 'layers',
        ownRoute: true,
        children: [
          {
            id: 'technologies-capabilities-domains',
            path: '/technology-domains',
            label: 'Domains',
            icon: 'dns',
          },
        ],
      },
    ],
  },
  { id: 'information', path: '/information', label: 'Data Domains', icon: 'data_matrix' },
  { id: 'tags', path: '/tags', label: 'Tags', icon: 'label' },
  {
    id: 'admin',
    path: '/admin',
    label: 'Admin',
    icon: 'admin_panel_settings',
    permission: 'audit:view',
    children: [
      {
        id: 'admin-audit',
        path: '/audit',
        label: 'Audit Log',
        icon: 'history',
        permission: 'audit:view' as Permission,
      },
      {
        id: 'admin-analytics',
        path: '/analytics',
        label: 'Analytics',
        icon: 'analytics',
        permission: 'analytics:view' as Permission,
      },
      {
        id: 'admin-users',
        path: '/admin/users',
        label: 'Users',
        icon: 'group',
        permission: 'user:manage' as Permission,
      },
      {
        id: 'admin-bulk-operations',
        path: '/admin/bulk-operations',
        label: 'Bulk Operations',
        icon: 'batch_prediction',
        permission: 'bulk-operations:write' as Permission,
      },
      {
        id: 'admin-model-package',
        path: '/admin/model-package',
        label: 'Export / Import',
        icon: 'archive',
        permission: 'model-package:export' as Permission,
      },
    ],
  },
];

// path === '/' needs an exact match (everything starts with '/'); every
// other path matches itself or any of its own sub-routes (`/x` or `/x/...`).
const isPathActive = (pathname: string, path: string): boolean =>
  path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);

// This node's own path plus every path in its subtree, depth-first.
const collectPaths = (node: NavNode): string[] => [
  node.path,
  ...(node.children?.flatMap(collectPaths) ?? []),
];

const flattenPaths = (nodes: NavNode[]): string[] => nodes.flatMap(collectPaths);

// Every node that has children, at any depth — these are the ones that
// need expand/collapse state.
const collectGroupNodes = (nodes: NavNode[]): NavNode[] =>
  nodes.flatMap((n) => (n.children ? [n, ...collectGroupNodes(n.children)] : []));

// A node's own header row should render as a clickable Link (rather than a
// plain label) when its own path is itself a real, navigable page: either
// a direct child shares that exact path (the "self" child pattern used by
// Applications/Technologies), or it's explicitly flagged via `ownRoute`.
const hasOwnRoute = (node: NavNode): boolean =>
  !!node.ownRoute || (node.children?.some((c) => c.path === node.path) ?? false);

/**
 * "Most specific match wins", generalized to arbitrary nesting depth: a
 * node is active only when the current route falls within its own subtree
 * AND no *other* node in the whole tree — one whose path is not part of
 * this node's own subtree — has a longer, more specific path that also
 * matches. This is what keeps e.g. "Products" (/technologies) from staying
 * highlighted while on the sibling "Tech Radar" (/technologies/radar), while
 * still letting a group and its truly-active descendant highlight together
 * (e.g. "Capabilities" and "Domains" both selected while viewing a domain).
 */
const isNodeActive = (pathname: string, allPaths: string[], node: NavNode): boolean => {
  const ownPaths = collectPaths(node);
  const withinOwnSubtree = ownPaths.some((p) => isPathActive(pathname, p));
  if (!withinOwnSubtree) return false;

  const suppressedByMoreSpecificPath = allPaths.some(
    (p) => !ownPaths.includes(p) && p.startsWith(`${node.path}/`) && isPathActive(pathname, p)
  );
  return !suppressedByMoreSpecificPath;
};

export default function Sidebar({ slot }: SidebarProps): JSX.Element {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const collapsed = useAppSelector(selectSidebarCollapsed);
  const width = useAppSelector(selectSidebarWidth);

  // Drag-to-resize (issue #1604). `dragStateRef` holds the in-flight drag's
  // starting pointer x / width so `handlePointerMove` can compute a delta
  // without re-subscribing listeners on every pixel of movement. `dragWidth`
  // (component state, not a ref onto the forge-drawer element) drives the
  // live width shown while dragging — forge-react's web-component proxy only
  // accepts an object ref for its `ref` prop, not a callback ref, so a
  // drawerRef + imperative `style.setProperty` here would crash every page
  // that renders the app shell (forge-react's reactify-wc wrapper does
  // `this.ref = this.props.innerRef || createRef()`, and a callback ref has
  // no `.current`). Going through React state instead sidesteps that
  // entirely. Only the final width is dispatched to Redux, on pointer up.
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const liveWidth = dragWidth ?? width;

  const handlePointerMove = useCallback((e: PointerEvent): void => {
    const drag = dragStateRef.current;
    if (!drag) return;
    const next = Math.min(
      SIDEBAR_MAX_WIDTH,
      Math.max(SIDEBAR_MIN_WIDTH, drag.startWidth + (e.clientX - drag.startX))
    );
    setDragWidth(next);
  }, []);

  const handlePointerUp = useCallback((): void => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    setDragWidth((current) => {
      if (current !== null) dispatch(setSidebarWidth(current));
      return null;
    });
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
  }, [dispatch, handlePointerMove]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    dragStateRef.current = { startX: e.clientX, startWidth: width };
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const delta = e.key === 'ArrowRight' ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP;
      dispatch(setSidebarWidth(width + delta));
    }
  };

  // Drop any in-flight drag's document-level listeners if the sidebar
  // unmounts mid-drag (e.g. route change tears down the layout).
  useEffect(
    () => (): void => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp]
  );

  const canViewAudit = usePermission('audit:view');
  const canViewAnalytics = usePermission('analytics:view');
  const canManageUsers = usePermission('user:manage');
  const canBulkWrite = usePermission('bulk-operations:write');
  const canExportModelPackage = usePermission('model-package:export');

  const permissionMap: Partial<Record<Permission, boolean>> = {
    'audit:view': canViewAudit,
    'analytics:view': canViewAnalytics,
    'user:manage': canManageUsers,
    'bulk-operations:write': canBulkWrite,
    'model-package:export': canExportModelPackage,
  };

  // p is a Permission enum value — not user-controlled
  // eslint-disable-next-line security/detect-object-injection
  const hasPermission = (p?: Permission): boolean => !p || permissionMap[p] === true;

  // A group is visible if any (recursively filtered) child remains visible;
  // a leaf is visible if the caller has its own permission.
  const filterVisible = (node: NavNode): NavNode | null => {
    if (node.children) {
      const children = node.children.map(filterVisible).filter((c): c is NavNode => c !== null);
      return children.length > 0 ? { ...node, children } : null;
    }
    return hasPermission(node.permission) ? node : null;
  };

  const visibleItems = navItems.map(filterVisible).filter((n): n is NavNode => n !== null);
  const allPaths = flattenPaths(visibleItems);
  const groupNodes = collectGroupNodes(visibleItems);

  const initialExpanded = (): Record<string, boolean> =>
    Object.fromEntries(groupNodes.map((n) => [n.id, isNodeActive(location.pathname, allPaths, n)]));

  const [expanded, setExpanded] = useState<Record<string, boolean>>(initialExpanded);

  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      for (const node of groupNodes) {
        if (isNodeActive(location.pathname, allPaths, node)) next[node.id] = true;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // intentional: only re-run on path change

  const toggleGroup = (id: string): void => {
    // id is a nav node's internal constant key — not user-controlled
    // eslint-disable-next-line security/detect-object-injection
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const depthSuffix = (depth: number): string =>
    depth === 0 ? '' : depth === 1 ? ' nav-subitem' : ' nav-subsubitem';

  const renderNode = (node: NavNode, depth: number): JSX.Element => {
    const active = isNodeActive(location.pathname, allPaths, node);

    if (node.children) {
      const ownRoute = hasOwnRoute(node);
      const isExpanded = !!expanded[node.id];
      return (
        <div key={node.id} className="nav-group">
          <ForgeListItem
            className={`nav-item nav-group-header${depthSuffix(depth)}`}
            selected={active}
            onClick={() => toggleGroup(node.id)}
            wrap
          >
            <ForgeIcon slot="start" name={node.icon} aria-hidden="true" />
            {ownRoute ? (
              <Link
                to={node.path}
                className="nav-link"
                aria-current={active ? 'page' : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
              >
                {node.label}
              </Link>
            ) : (
              <span className="nav-link nav-group-label">{node.label}</span>
            )}
            <ForgeIcon
              slot="end"
              name={isExpanded ? 'expand_less' : 'expand_more'}
              aria-hidden="true"
            />
          </ForgeListItem>
          {isExpanded && (
            // Use div instead of forge-list to avoid nesting role="list" inside
            // role="list" (forge-list sets role="list" on its host element).
            // forge-list-item children are owned by the outer nav-list as listitems.
            <div className="nav-sublist">
              {node.children.map((child) => renderNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <ForgeListItem
        key={node.id}
        className={`nav-item${depthSuffix(depth)}`}
        selected={active}
        wrap
      >
        <ForgeIcon slot="start" name={node.icon} aria-hidden="true" />
        <Link to={node.path} className="nav-link" aria-current={active ? 'page' : undefined}>
          {node.label}
        </Link>
      </ForgeListItem>
    );
  };

  // `--forge-drawer-width` (per Sidebar.css) is how `forge-drawer` sizes its
  // own box — wiring the redux-backed width through it (rather than a plain
  // CSS `width`) is what lets `forge-drawer`'s `dismissible` mode still
  // collapse the box to 0 on `open=false`, which is what makes the
  // `forge-scaffold` `body-left` slot reflow `.app-content` on toggle (#1604).
  const drawerStyle: SidebarDrawerStyle = { '--forge-drawer-width': `${liveWidth}px` };

  return (
    // ForgeDrawer must stay the sole top-level element `slot={slot}` is set
    // on — the `slot` attribute only takes effect on a *direct* light-DOM
    // child of the shadow host (forge-scaffold); wrapping it in an extra
    // `<div>` (an earlier version of this fix did) would silently drop the
    // drawer out of the `body-left` slot entirely. The resize handle instead
    // renders *inside* the drawer's own default-slot content, positioned via
    // `.app-sidebar { position: relative }` (Sidebar.css) so it's anchored to
    // the drawer's own box regardless of that box's shadow-internal markup.
    <ForgeDrawer
      slot={slot}
      open={!collapsed}
      mode="dismissible"
      className="app-sidebar"
      style={drawerStyle}
    >
      <div className="sidebar-content">
        <div className="sidebar-header">
          <h2 className="forge-typography--subtitle1">EA Modeling</h2>
        </div>

        <nav aria-label="Main navigation">
          <ForgeList className="nav-list">
            {visibleItems.map((item) => renderNode(item, 0))}
          </ForgeList>
        </nav>
      </div>
      {!collapsed && (
        <div
          className="sidebar-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={liveWidth}
          aria-valuemin={SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
        />
      )}
    </ForgeDrawer>
  );
}
